import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || '';
const ACCENT = '#00d4aa';

const STAT_LABELS = {
  para:      { en: 'paragraphs', ar: 'فقرة',     fa: 'پاراگراف' },
  heading:   { en: 'headings',   ar: 'عنوان',    fa: 'عنوان' },
  math:      { en: 'equations',  ar: 'معادلة',   fa: 'معادله' },
  caption:   { en: 'captions',   ar: 'تسمية',    fa: 'کپشن' },
  figures:   { en: 'figures',    ar: 'شكل',      fa: 'تصویر' },
  footnote:  { en: 'footnotes',  ar: 'هامش',     fa: 'پاورقی' },
  pageimage: { en: 'pages as image', ar: 'صفحة كصورة', fa: 'صفحه به صورت تصویر' },
};

export default function RtlPdfWordCard({ lang }) {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const isRTL = lang === 'ar' || lang === 'fa';

  const onDrop = useCallback((accepted) => {
    const valid = accepted.filter(f => /\.pdf$/i.test(f.name));
    if (valid.length < accepted.length) {
      toast.error(
        lang === 'ar' ? 'يُسمح فقط بملفات PDF'
        : lang === 'fa' ? 'فقط فایل PDF مجاز است'
        : 'Only PDF files are allowed'
      );
    }
    if (valid.length > 0) {
      setFile(valid[0]);
      setJobId(null);
      setStats(null);
    }
  }, [lang]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false,
  });

  const handleConvert = async () => {
    if (!file) return;
    setLoading(true);
    setJobId(null);
    setStats(null);
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const res = await axios.post(`${API}/api/convert/pdf-to-word-rtl`, formData, {
        headers,
        timeout: 300000,
      });
      setJobId(res.data.job_id);
      setStats(res.data.stats || {});
      toast.success(
        lang === 'ar' ? 'تم التحويل! اختر صيغة التنزيل 🎉'
        : lang === 'fa' ? 'تبدیل شد! فرمت دانلود را انتخاب کنید 🎉'
        : 'Converted! Choose your download format 🎉',
        { style: { background: '#13131f', color: ACCENT, border: `1px solid ${ACCENT}40` } }
      );
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل التحويل' : lang === 'fa' ? 'تبدیل ناموفق' : 'Conversion failed';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const download = async (format) => {
    if (!jobId) return;
    setDownloading(format);
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const res = await axios.get(
        `${API}/api/convert/pdf-to-word-rtl/${jobId}${format === 'latex' ? '?format=latex' : ''}`,
        { responseType: 'blob', headers, timeout: 60000 }
      );
      const filename = format === 'latex' ? 'rtlconv_latex.zip' : `${file.name.replace(/\.pdf$/i, '')}.docx`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let msg = lang === 'ar' ? 'فشل التنزيل' : lang === 'fa' ? 'دانلود ناموفق' : 'Download failed';
      if (err.response?.data instanceof Blob) {
        try { const t = await err.response.data.text(); msg = JSON.parse(t).detail || msg; } catch { }
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const lbl = {
    drop:       lang === 'ar' ? 'اسحب ملف PDF هنا أو انقر للاختيار'        : lang === 'fa' ? 'فایل PDF را اینجا بکشید یا کلیک کنید'         : 'Drag a PDF here or click to select',
    limit:      lang === 'ar' ? 'PDF واحد — حتى 50 ميغابايت'                : lang === 'fa' ? 'یک PDF — حداکثر ۵۰ مگابایت'                    : 'Single PDF — up to 50 MB',
    convert:    lang === 'ar' ? '⚡ تحويل الآن'                               : lang === 'fa' ? '⚡ تبدیل کن'                                     : '⚡ Convert Now',
    converting: lang === 'ar' ? 'جاري التحويل...'                            : lang === 'fa' ? 'در حال تبدیل...'                               : 'Converting...',
    dlDocx:     lang === 'ar' ? 'تنزيل Word (.docx)'                        : lang === 'fa' ? 'دانلود Word (.docx)'                           : 'Download Word (.docx)',
    dlLatex:    lang === 'ar' ? 'تنزيل LaTeX (.zip)'                        : lang === 'fa' ? 'دانلود LaTeX (.zip)'                           : 'Download LaTeX (.zip)',
    stats:      lang === 'ar' ? 'ملخص التحويل'                               : lang === 'fa' ? 'خلاصه تبدیل'                                   : 'Conversion summary',
    change:     lang === 'ar' ? 'تغيير الملف'                                : lang === 'fa' ? 'تغییر فایل'                                     : 'Change file',
  };

  return (
    <div>
      {!file ? (
        <div
          {...getRootProps()}
          style={{
            border: `2px dashed ${isDragActive ? ACCENT : 'rgba(255,255,255,0.15)'}`,
            borderRadius: '12px',
            padding: '36px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: isDragActive ? `${ACCENT}0a` : 'rgba(255,255,255,0.02)',
            transition: 'all 0.2s',
            marginBottom: '16px',
          }}
        >
          <input {...getInputProps()} />
          <div style={{ fontSize: '36px', marginBottom: '10px' }}>📄</div>
          <div style={{ color: 'rgba(255,255,255,0.62)', fontSize: '14px' }}>{lbl.drop}</div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '6px' }}>{lbl.limit}</div>
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          background: `${ACCENT}0e`, border: `1px solid ${ACCENT}35`,
          borderRadius: '10px', padding: '10px 14px', marginBottom: '14px',
          direction: isRTL ? 'rtl' : 'ltr',
        }}>
          <span style={{ fontSize: '20px' }}>📄</span>
          <span style={{ flex: 1, fontSize: '13px', color: 'rgba(255,255,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </span>
          <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', flexShrink: 0 }}>
            {(file.size / 1024 / 1024).toFixed(1)} MB
          </span>
          <button
            onClick={() => { setFile(null); setJobId(null); setStats(null); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer', padding: '2px 6px', flexShrink: 0 }}
          >
            {lbl.change}
          </button>
        </div>
      )}

      {!jobId && (
        <button
          className="btn-primary"
          onClick={handleConvert}
          disabled={!file || loading}
          style={{ opacity: !file ? 0.5 : 1, background: `linear-gradient(135deg, ${ACCENT}, #00b38a)` }}
        >
          {loading ? <><span className="spinner" />{lbl.converting}</> : lbl.convert}
        </button>
      )}

      {jobId && stats && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Stats summary */}
          {Object.keys(stats).length > 0 && (
            <div style={{
              background: 'rgba(0,212,170,0.06)', border: '1px solid rgba(0,212,170,0.2)',
              borderRadius: '10px', padding: '10px 14px',
            }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                {lbl.stats}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', direction: isRTL ? 'rtl' : 'ltr' }}>
                {Object.entries(stats).map(([kind, count]) => {
                  const label = (STAT_LABELS[kind] || {})[lang] || (STAT_LABELS[kind] || {}).en || kind;
                  return (
                    <span key={kind} style={{
                      background: 'rgba(0,212,170,0.12)', border: '1px solid rgba(0,212,170,0.25)',
                      borderRadius: '6px', padding: '3px 9px', fontSize: '11.5px', color: ACCENT, fontWeight: 600,
                    }}>
                      {count} {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Download buttons */}
          <button
            className="btn-primary"
            onClick={() => download('docx')}
            disabled={downloading !== null}
            style={{ background: `linear-gradient(135deg, ${ACCENT}, #00b38a)` }}
          >
            {downloading === 'docx' ? <><span className="spinner" />{lang === 'ar' ? 'جاري التنزيل...' : lang === 'fa' ? 'در حال دانلود...' : 'Downloading...'}</> : lbl.dlDocx}
          </button>
          <button
            onClick={() => download('latex')}
            disabled={downloading !== null}
            style={{
              width: '100%', padding: '11px', borderRadius: '10px', fontSize: '13.5px', fontWeight: 600,
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.75)', cursor: downloading !== null ? 'not-allowed' : 'pointer',
              opacity: downloading !== null ? 0.6 : 1, transition: 'all 0.15s',
            }}
            onMouseEnter={e => { if (downloading === null) e.currentTarget.style.borderColor = `${ACCENT}60`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
          >
            {downloading === 'latex' ? <><span className="spinner" />{lang === 'ar' ? 'جاري التنزيل...' : lang === 'fa' ? 'در حال دانلود...' : 'Downloading...'}</> : lbl.dlLatex}
          </button>
          <button
            onClick={() => { setJobId(null); setStats(null); setFile(null); }}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', fontSize: '12px', cursor: 'pointer', padding: '4px 0' }}
          >
            {lang === 'ar' ? 'تحويل ملف آخر' : lang === 'fa' ? 'تبدیل فایل دیگر' : 'Convert another file'}
          </button>
        </div>
      )}
    </div>
  );
}
